import type React from "react"
import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Card, Divider, Button } from "antd"
import { ReloadOutlined } from "@ant-design/icons"
import { IconBook2 } from "@tabler/icons-react"
import { PAGE_CONFIG, PAGINATION_CONFIG } from "@/constants/table"
import { useTableParams } from "@/hooks/useTableParams"
import { logService } from "@/services/logApi"
import { ReadingPracticeFilterForm, type ReadingPracticeFilterValues } from "./components/ReadingPracticeFilterForm"
import { ReadingPracticeTable } from "./components/ReadingPracticeTable"
import { ReadingPracticeDetailModal } from "./components/ReadingPracticeDetailModal"
import type { ReadingPracticeLogItem } from "./types"

export interface ReadingPracticeQueryParams {
  page: number
  per_page: number
  search?: string
  status?: string
  difficulty?: string
  selected_type?: string
  start_date?: string
  end_date?: string
}

export const ReadingPracticeLogPage: React.FC = () => {
  const [searchParams, setSearchParams] = useTableParams<ReadingPracticeQueryParams>({
    ...PAGE_CONFIG,
    search: "",
    status: undefined,
    difficulty: undefined,
    selected_type: undefined,
    start_date: undefined,
    end_date: undefined,
  })

  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState<ReadingPracticeLogItem | null>(null)
  const [selectedIndex, setSelectedIndex] = useState<number>(0)

  // Fetch practice logs query
  const { data, isFetching, refetch } = useQuery({
    queryKey: ["reading_practice_logs_list", searchParams],
    queryFn: () =>
      logService.listReadingPracticeLogs({
        page: searchParams.page,
        limit: searchParams.per_page,
        search: searchParams.search ? searchParams.search.trim() : undefined,
        status: searchParams.status || undefined,
        difficulty: searchParams.difficulty || undefined,
        selected_type: searchParams.selected_type || undefined,
        start_date: searchParams.start_date || undefined,
        end_date: searchParams.end_date || undefined,
      }),
  })

  const handleSearch = (values: ReadingPracticeFilterValues) => {
    setSearchParams({
      ...searchParams,
      search: values.search,
      status: values.status,
      difficulty: values.difficulty,
      selected_type: values.selected_type,
      start_date: values.start_date,
      end_date: values.end_date,
      page: 1,
    })
  }

  const items = (data?.data || []) as ReadingPracticeLogItem[]
  const totalCount = data?.total ?? 0

  return (
    <div className="flex flex-col gap-2">
      {/* 1. Filter form */}
      <ReadingPracticeFilterForm
        onSearch={handleSearch}
        loading={isFetching}
        initialValues={{
          search: searchParams.search,
          status: searchParams.status,
          difficulty: searchParams.difficulty,
          selected_type: searchParams.selected_type,
          start_date: searchParams.start_date,
          end_date: searchParams.end_date,
        }}
      />

      {/* 2. Main Data Card & Table */}
      <Card
        className="flex min-h-0 grow flex-col shadow-xs"
        classNames={{ body: "flex grow flex-col" }}
        title={
          <div className="flex items-center justify-between py-1">
            <div className="flex items-center gap-2">
              <IconBook2 className="text-blue-500" size={20} />
              <span className="font-semibold text-base text-gray-800">Log Làm Bài Tập Đọc (Reading Practice Logs)</span>
              <Divider orientation="vertical" />
              <span className="font-normal text-gray-500 text-sm">
                Tổng số lượt: <strong className="text-gray-700">{totalCount}</strong>
              </span>
            </div>
            <Button icon={<ReloadOutlined spin={isFetching} />} onClick={() => refetch()} loading={isFetching}>
              Làm mới
            </Button>
          </div>
        }
      >
        <ReadingPracticeTable
          data={items}
          loading={isFetching}
          onViewDetail={(record, index) => {
            setSelectedRecord(record)
            setSelectedIndex(index)
            setDetailModalOpen(true)
          }}
          pagination={{
            pageSize: searchParams.per_page,
            current: searchParams.page,
            total: totalCount,
            ...PAGINATION_CONFIG,
            onChange(page: number, pageSize: number) {
              setSearchParams({
                ...searchParams,
                page,
                per_page: pageSize,
              })
            },
          }}
        />
      </Card>

      {/* 4. Fullscreen Split-view Detail Modal */}
      <ReadingPracticeDetailModal
        open={detailModalOpen}
        record={selectedRecord}
        items={items}
        currentIndex={selectedIndex}
        onNavigate={(newIndex) => {
          setSelectedIndex(newIndex)
          setSelectedRecord(items[newIndex] || null)
        }}
        onClose={() => {
          setDetailModalOpen(false)
          setSelectedRecord(null)
          setSelectedIndex(0)
        }}
      />
    </div>
  )
}

export default ReadingPracticeLogPage
